import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { LicenseFacade } from 'app/shared/services/facades/license.facade';
import {
  LocaleDatePipe,
  SearchExpression,
  Destroy,
  isMobile,
  EventAggregator,
  Navigator,
  SearchConfig,
  Types,
  tr,
  domHandler,
  NavigatorHelperBase,
  Key,
} from '@kpd/framework-ngx';
import { takeUntil } from 'rxjs/operators';
import { License } from 'app/shared/models/license';
import { LicensesNavigatorHelper } from 'app/licenses/licenses-navigator-helper';
import { flattenArrays } from 'app/shared/utils/array';
import { of } from 'rxjs';

@Component({
  selector: 'app-licenses',
  templateUrl: './licenses-overview.component.html',
})
export class LicensesComponent implements OnInit, OnDestroy {
  @Destroy()
  destroy$: any;
  loading = false;
  licenses: License[] = [];
  expiredLicenses: License[] = [];
  activeLicenses: License[] = [];
  searchExpression: SearchExpression;
  selection = {};
  selectedIds: Key<string>[] = [];
  currentIndex: number;
  itemsTotalCount: number;
  navigatorHelper: NavigatorHelperBase;

  get allApplications(): {
    name: string;
    description?: string;
  }[] {
    const arrayOfApplications = flattenArrays(this.licenses.map((l) => l.applications || []));
    const arrayOfApplicationNames = arrayOfApplications.map((a) => a.name);
    const uniqueNames = Array.from(new Set(arrayOfApplicationNames));
    return uniqueNames.map((name) => {
      const app = arrayOfApplications.find((a) => a.name === name);
      const display = `${name} ${app && app.description ? ' - ' + app.description : ''}`;
      return {
        name: name,
        display: display,
      };
    });
  }

  searchConfig = this.getSearchConfig();

  @ViewChild('overview', { static: true })
  overview: any;
  showExpired: boolean;

  constructor(
    private navigator: Navigator,
    public datePipe: LocaleDatePipe,
    public entityFacade: LicenseFacade,
    private eventAggregator: EventAggregator,
  ) {
    this.navigatorHelper = new LicensesNavigatorHelper(navigator);
  }

  ngOnInit(): void {
    this.entityFacade.loading$.subscribe((result) => {
      this.loading = !!(result && result.overviewLoading && result.overviewLoading.length > 0);
    });
    this.entityFacade.loadInitialData();
    this.entityFacade.items$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
      this.itemsTotalCount = items.length;
      this.licenses = items;
      this.expiredLicenses = items.filter((l) => l.isExpired);
      this.activeLicenses = items.filter((l) => !l.isExpired);

      this.entityFacade.excludeFromSelection = this.expiredLicenses.map((l) => l.key);
      setTimeout(() => {
        this.scrollToFirstSelected();
      });
    });

    this.entityFacade.selectedIds$.pipe(takeUntil(this.destroy$)).subscribe((ids) => {
      this.selectedIds = ids as Key<string>[];
      ids.forEach((id) => {
        this.selection[id.toString()] = true;
      });
    });
  }

  private isItemVisible(licenseId: Key<string>): boolean {
    const license = this.licenses ? this.licenses.find((l) => l.key.toString() === licenseId.toString()) : null;
    if (license && !license.isExpired) {
      return true;
    }

    return this.showExpired;
  }

  private scrollToFirstSelected(): void {
    const list = domHandler.find(
      this.overview.nativeElement.querySelector('.scroll-view__content'),
      '.license--selected',
    ) as any;
    if (list && list.length > 0) {
      const targetElement = list.item(0);

      if (!domHandler.isElementInView(targetElement)) {
        targetElement.scrollIntoView({ inline: 'center', block: 'center' });
        return;
      }
    }
  }

  toggleExpired(): void {
    if (!this.showExpired) {
      this.entityFacade.changeSelection(
        -1,
        this.selectedIds.filter((id) => this.isItemVisible(id)),
      );

      this.entityFacade.excludeFromSelection = this.expiredLicenses.map((l) => l.key);
    } else {
      this.entityFacade.excludeFromSelection = [];
    }
  }

  onCommand(command: string): void {
    switch (command) {
      case 'view':
        const licenseKey = this.selectedIds[0];
        this.navigatorHelper.navigateToConsult(licenseKey);
        break;
      case 'sidepanel':
        this.eventAggregator.publish('toggleSidePanel', true);
        break;
    }
  }

  onRefresh(): void {
    this.overview.nativeElement.querySelector('.scroll-body').scrollTop = 0;
    this.entityFacade.reloadData();
  }

  onSearch(searchExpression: SearchExpression): void {
    this.searchExpression = searchExpression;
    this.entityFacade.loadData(undefined, this.searchExpression);
  }

  isSelected(license: License): boolean {
    return this.selectedIds.some(
      (licenseKey: Key<string>) => licenseKey && licenseKey.toString() === license.key.toString(),
    );
  }

  onLicenseClicked(event: MouseEvent, license: License): void {
    if (event.shiftKey) {
      return;
    }
    if (!event.ctrlKey && !event.metaKey && !isMobile.any() && !event.shiftKey) {
      // De-select everything if no mod-keys pressed and not on mobile
      this.selection = {};
    }
    const clickedItemIndex = this.licenses.findIndex((i) => i.key.toString() === license.key.toString());
    // Shift: select from last selected (or beginning) up till the clicked item

    // Toggle selection of clicked item
    if (this.selection[license.key.toString()] === true) {
      this.selection[license.key.toString()] = false;
      this.currentIndex = 0;
    } else {
      this.selection[license.key.toString()] = true;
      this.currentIndex = clickedItemIndex;
    }

    // Send the new selection to the facade/store
    const selectedLicenses: License[] = this.licenses.filter((l) => this.selection[l.key.toString()] === true);
    const index = this.licenses.findIndex((l) => l.key.toString() === license.key.toString());
    this.entityFacade.changeSelection(
      index,
      selectedLicenses.map((l) => l.key),
    );
  }

  getPreviousIndex(ids: string[], clickedIndex: number): number {
    // Indexes of all selected rows, sorted
    const selectedIndexes = ids
      .filter((key) => this.selection[key] === true)
      .map((id) => ids.findIndex((i) => i === id))
      .sort();

    const first = selectedIndexes[0];
    const last = selectedIndexes[selectedIndexes.length - 1];
    if (clickedIndex < first) {
      return first;
    }
    return last;
  }

  onClickAssigned(key: Key<string>): void {
    if (!this.selectedIds.some((licenseKey) => licenseKey.toString() === key.toString())) {
      this.entityFacade.clearSelection();
    }
    this.navigator.navigateChild(['licenses', 'details', 'license', key.id], {
      queryParams: { tab: 'users' },
    });
  }

  getSearchConfig(): SearchConfig {
    return new SearchConfig({
      multipleCriteria: true,
      fields: [
        {
          field: 'applicationNames',
          displayValue: tr('Application'),
          dataType: Types.Array,
          entityConfig: {
            displayField: 'display',
            lookupField: 'name',
            completeMethod: (value: string) => {
              if (!value) {
                return of(this.allApplications);
              }
              return of(
                this.allApplications.filter((app) => {
                  return app.name.toUpperCase().includes(value.toUpperCase());
                }),
              );
            },
          },
        },
        {
          field: 'clientId',
          displayValue: tr('Client'),
          dataType: Types.String,
        },

        {
          field: 'purchaseDate',
          displayValue: tr('Purchase date'),
          dataType: Types.Date,
          excludeFromGlobal: true,
        },
        {
          field: 'validFromDate',
          displayValue: tr('Valid from'),
          dataType: Types.Date,
          excludeFromGlobal: true,
        },
        {
          field: 'validUntilDate',
          displayValue: tr('Valid until'),
          dataType: Types.Date,
          excludeFromGlobal: true,
        },
        {
          field: 'quantity',
          displayValue: tr('Quantity'),
          dataType: Types.Integer,
          excludeFromGlobal: true,
        },
        {
          field: 'quantityAssigned',
          displayValue: tr('Quantity assigned'),
          dataType: Types.Integer,
          excludeFromGlobal: true,
        },
        {
          field: 'quantityAvailable',
          displayValue: tr('Quantity available'),
          dataType: Types.Integer,
          excludeFromGlobal: true,
        },
      ],
    });
  }

  ngOnDestroy(): void {
    // Needed for custom destroy decorator
  }
}
